import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Wallet, Check } from "lucide-react";

const STATUS_COLORS = {
  draft:      "text-foreground/60 border-foreground/20",
  preparing:  "text-amber-500 border-amber-500/40",
  finalized:  "text-emerald-500 border-emerald-500/40",
  paid:       "text-foreground/40 border-foreground/15",
  cancelled:  "text-red-500 border-red-500/40",
};

const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0,10); };

export default function PayrollPeriodsPage() {
  const { branchId, hasPerm } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const params = branchId ? { branch_id: branchId } : {};
      const { data } = await api.get("/pay-periods", { params });
      setPeriods(data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, [branchId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error("Select a branch first"); return; }
    try {
      await api.post("/pay-periods", {
        branch_id: branchId,
        name: form.name,
        starts_on: form.starts_on,
        ends_on: form.ends_on,
        notes: form.notes,
      });
      toast.success("Pay period drafted");
      setCreating(false); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? periods : periods.filter((p) => p.status === filter);
  const totals = useMemo(() => {
    const finals = periods.filter((p) => ["finalized","paid"].includes(p.status));
    const paid = periods.filter((p) => p.status === "paid");
    return {
      final_net: finals.reduce((s, p) => s + (p.totals_snapshot?.net_pay || 0), 0),
      paid_net:  paid.reduce((s, p) => s + (p.totals_snapshot?.net_pay || 0), 0),
      open:      periods.filter((p) => ["draft","preparing"].includes(p.status)).length,
    };
  }, [periods]);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="payroll-periods-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Payroll · Periods</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Pay runs</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Each pay period snapshots timesheet hours + event commissions into per-employee payslips. Prepare, adjust, finalise, mark paid — the numbers lock the moment you finalise.
          </p>
        </div>
        {hasPerm("payroll.prepare") && (
          <button onClick={() => setCreating(true)} data-testid="period-new-btn" className="btn-pill btn-pill-solid h-10">
            <Plus size={14}/> New period
          </button>
        )}
      </div>

      <div className="mt-14 grid grid-cols-3 gap-x-12 gap-y-6 animate-fade-up animate-fade-up-1">
        <Stat num=".01" label="Open periods" value={totals.open}/>
        <Stat num=".02" label="Finalized net" value={peso(totals.final_net)}/>
        <Stat num=".03" label="Paid net (all-time)" value={peso(totals.paid_net)} accent/>
      </div>

      <div className="mt-10 flex items-center gap-2 animate-fade-up animate-fade-up-2">
        {["all","draft","preparing","finalized","paid","cancelled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
                  data-testid={`period-filter-${f}`}
                  className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                    filter === f ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                  }`}>{f}</button>
        ))}
      </div>

      <div className="mt-8 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-3" data-testid="period-table">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-3">Period</div>
          <div className="col-span-2">Range</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Employees</div>
          <div className="col-span-2 text-right">Gross</div>
          <div className="col-span-2 text-right">Net</div>
        </div>
        {filtered.length === 0 && <div className="py-8 ink-subtle text-center text-sm">No pay periods in this bucket.</div>}
        {filtered.map((p) => {
          const t = p.totals_snapshot || {};
          return (
            <Link key={p.id} to={`/payroll/periods/${p.id}`} data-testid={`period-row-${p.id}`}
                  className="grid grid-cols-12 py-3 items-center hover:bg-foreground/[0.03] transition-colors">
              <div className="col-span-3 pr-2">
                <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{p.name}</div>
                <div className="text-xs ink-subtle italic truncate mt-0.5">{p.notes || "—"}</div>
              </div>
              <div className="col-span-2 mono text-xs ink">{p.starts_on} → {p.ends_on}</div>
              <div className="col-span-2">
                <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[p.status]}`}>
                  {p.status}
                </span>
              </div>
              <div className="col-span-1 text-right mono text-sm ink">{t.employees ?? "—"}</div>
              <div className="col-span-2 text-right mono text-sm ink">{t.gross_pay != null ? peso(t.gross_pay) : "—"}</div>
              <div className="col-span-2 text-right mono text-sm ink font-semibold">{t.net_pay != null ? peso(t.net_pay) : "—"}</div>
            </Link>
          );
        })}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="period-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCreating(false)}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Payroll · New period</div>
                <h2 className="mt-2 display-heading text-2xl ink">Draft a pay period</h2>
                <p className="mt-2 ink-muted text-xs">Periods can't overlap on the same branch. Prepare it after creation to snapshot timesheet + commissions.</p>
              </div>
              <button type="button" onClick={() => setCreating(false)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Name"><input required autoFocus value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="period-form-name" placeholder="July 1-15"/></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Starts on"><input required type="date" value={form.starts_on} onChange={(e)=>setForm({...form, starts_on: e.target.value})} className="i mono" data-testid="period-form-starts"/></F>
              <F label="Ends on"><input required type="date" value={form.ends_on} onChange={(e)=>setForm({...form, ends_on: e.target.value})} className="i mono" data-testid="period-form-ends"/></F>
            </div>
            <F label="Notes"><input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i"/></F>

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => setCreating(false)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="period-form-submit" className="btn-pill btn-pill-solid h-11 flex-1"><Wallet size={13}/> Create draft</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK = { name: "", starts_on: daysAgo(14), ends_on: daysAgo(1), notes: "" };

function Stat({ num, label, value, accent }) {
  return (
    <div data-testid={`period-stat-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono ink text-2xl font-semibold ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
